// Extracted from pages/Articles/CreateArticle.js (behavior-preserving move).
// Functions are recreated on every render with the latest wizard state, exactly as
// they were when they lived inside the page component. `log` is the original no-op.
import { directusAPI } from '../../../api';
import { getStoredToken, log } from '../services/articleGenerationApi';
import { useBulkGeneration } from './useBulkGeneration';
import { useDraftGeneration } from './useDraftGeneration';
import { useFinalArticleGeneration } from './useFinalArticleGeneration';
import { useSummaryGeneration } from './useSummaryGeneration';

/**
 * All generation flows of the "create article" wizard.
 * `ctx` carries the wizard state, setters and refs owned by the page.
 */
export function useArticleGeneration(ctx) {
  const {
    t,
    searchParams,
    articleContextRef,
    articleType,
    loading,
    setLoading,
    contentType,
    currentStep,
    articleContext,
    setArticleContext,
    selectedArticleForSummary,
    knowledgeMode,
    bulkTopic,
    setBulkTopicError,
    articleData,
    generatedArticleId,
    setSaveError,
    setIsDraftGenerated,
    syncArticleContextFromEditor,
  } = ctx;

  const draft = useDraftGeneration(ctx);
  const { queueAPI, createArticleRecord, autoGenerateAndRender } = draft;
  const handleBulkGeneration = useBulkGeneration(ctx);
  const handleSummaryGeneration = useSummaryGeneration({ ...ctx, queueAPI, createArticleRecord });
  const final = useFinalArticleGeneration({ ...ctx, queueAPI });

  const handleSubmit = async () => {
    log('🔘 SUBMIT: handleSubmit called', {
      loading,
      articleType,
      contentType,
      currentStep,
      articleContext: articleContext?.substring(0, 50),
      selectedArticleForSummary: !!selectedArticleForSummary,
      knowledgeMode
    });

    if (loading) {
      log('🔘 SUBMIT: Already loading, returning early');
      return;
    }

    try {
      setLoading(true);

      if (articleType === 'bulk') {
        // Handle bulk article generation
        await handleBulkGeneration();
        return;
      }

      // Resolve context from EditorJS first (single article), then local/db fallback
      let resolvedArticleContext = (articleContextRef.current || articleContext || '').trim();
      if (articleType === 'single' && contentType !== 'summary') {
        const editorContext = await syncArticleContextFromEditor();
        if (editorContext) {
          resolvedArticleContext = editorContext.trim();
        }
      }
      if (!resolvedArticleContext) {
        resolvedArticleContext = (articleData?.article_context || '').trim();
      }

      if (!resolvedArticleContext) {
        const currentArticleId = searchParams.get('id') || generatedArticleId;
        if (currentArticleId) {
          try {
            const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
            const response = await directusAPI.get(`/items/articles/${currentArticleId}`, {
              params: { fields: 'article_context' },
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            const dbContext = (response.data?.data?.article_context || '').trim();
            if (dbContext) {
              resolvedArticleContext = dbContext;
              setArticleContext(dbContext);
            }
          } catch (contextLoadError) {
            log('SUBMIT: Failed to load article_context fallback:', contextLoadError);
          }
        }
      }

      // Validate content based on article type
      if (articleType === 'bulk') {
        if (!bulkTopic || bulkTopic.trim() === '') {
          setBulkTopicError(t('articles.create.validation.bulkTopicRequired'));
          setSaveError(t('articles.create.validation.bulkTopicSaveError'));
          return;
        }
      } else {
        // Skip articleContext validation for summary - uses source selection instead
        if (contentType !== 'summary') {
          if (!resolvedArticleContext) {
            setSaveError(t('articles.create.validation.contextEmpty'));
            return;
          }
          if (resolvedArticleContext !== articleContext) {
            setArticleContext(resolvedArticleContext);
          }
        }
      }

      // 💾 DISABLED: No longer save early draft - article will be created after generation
      log('💾 SUBMIT: Proceeding with generation - article will be created with generated content');

      if (contentType === 'summary' && knowledgeMode === 'manual' && !selectedArticleForSummary) {
        setSaveError(t('articles.create.validation.selectScientificArticle'));
        return;
      }

      // Set article status to "ongoing" during draft generation
      if (generatedArticleId) {
        try {
          log('🔒 DRAFT: Setting article status to ongoing for draft generation');
          await directusAPI.patch(`/items/articles/${generatedArticleId}`, {
            status: 'ongoing'
          }, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getStoredToken()}`
            }
          });
          log('✅ DRAFT: Article status set to ongoing');
        } catch (error) {
          log('❌ DRAFT: Error setting ongoing status:', error);
        }
      }

      // Standardized flow: generate draft directly in step 2
      setIsDraftGenerated(false); // Reset to allow regeneration
      await autoGenerateAndRender();
    } catch (error) {
      console.error('Error:', error);
      setSaveError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    ...draft,
    ...final,
    handleBulkGeneration,
    handleSummaryGeneration,
    handleSubmit,
  };
}
